import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/* NIRU fix: toleranța (px) sub care considerăm că lista de produse din sertar
   NU debordează. Acoperă sub-pixeli, borduri și rotunjiri de layout. */
const NIRU_FBT_OVERFLOW_TOLERANCE = 8;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /* NIRU fix — starea acordeonului FBT („Cumpărate frecvent împreună”). */
  /** @type {boolean|null} Ultima valoare setată de noi; ne ajută să distingem
   *  o comutare automată de una făcută manual de client (evenimentul `toggle`
   *  al lui <details> este dispecerizat asincron, deci un simplu flag nu ajunge). */
  #niruFbtWanted = null;

  /** @type {MutationObserver|null} */
  #niruFbtObserver = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    /* NIRU fix */
    this.#niruFbtObserver?.disconnect();
    this.#niruFbtObserver = null;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#niruSyncFbt();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            /* NIRU fix: după un morph al sertarului markup-ul revine la starea
               implicită (deschis), deci re-aplicăm regula de spațiu. */
            this.#niruSyncFbt();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * NIRU fix — acordeonul „Cumpărate frecvent împreună” din sertarul de coș.
   *
   * Regula: la fiecare deschidere a sertarului (și după re-randarea coșului)
   * acordeonul pornește DESCHIS; dacă, deschis fiind, lista de produse
   * debordează zona vizibilă a sertarului cu mai mult de
   * `NIRU_FBT_OVERFLOW_TOLERANCE` px (adică produsele din coș ar fi împinse
   * afară din câmpul vizual), îl pliem la loc. Decizia se ia în același frame,
   * înainte de paint, deci nu există „salt” de layout.
   *
   * Rulează DOAR la deschiderea sertarului / după actualizarea coșului —
   * zero cost la încărcarea paginii. După ce clientul atinge manual acordeonul,
   * nu mai intervenim în sesiunea curentă.
   */
  #niruSyncFbt = () => {
    this.#niruFbtObserver?.disconnect();
    this.#niruFbtObserver = null;

    const details = /** @type {HTMLDetailsElement|null} */ (this.querySelector('details.cart-drawer__fbt'));
    if (!details) return;

    if (!details.dataset.niruFbtBound) {
      details.dataset.niruFbtBound = '1';
      details.addEventListener('toggle', () => {
        if (details.open === this.#niruFbtWanted) return; // comutare făcută de noi
        details.dataset.niruFbt = 'manual';
        this.#niruFbtObserver?.disconnect();
        this.#niruFbtObserver = null;
      });
    }

    const run = () => {
      if (!details.isConnected) return;
      this.#niruFbtDecide(details);

      /* Cardurile FBT se încarcă asincron (upsell-recommendations.js). Dacă lista
         este încă goală, re-evaluăm O SINGURĂ DATĂ, la apariția primului card. */
      const list = details.querySelector('[ref="recommendationsList"]');
      if (!list || list.children.length > 0) return;

      const observer = new MutationObserver(() => {
        if (list.children.length === 0) return;
        observer.disconnect();
        this.#niruFbtObserver = null;
        if (details.dataset.niruFbt !== 'manual') this.#niruFbtDecide(details);
      });
      observer.observe(list, { childList: true });
      this.#niruFbtObserver = observer;
    };

    const items = this.querySelector('.cart-drawer__items');
    if (items instanceof HTMLElement && items.clientHeight > 0) run();
    else requestAnimationFrame(run);
  };

  /**
   * @param {HTMLDetailsElement} details
   */
  #niruFbtDecide(details) {
    details.open = true;

    /* `scrollHeight - clientHeight` pe containerul care derulează. În modul
       `cart-summary-sticky="false"` derulează `.cart-drawer__content`, altfel
       `.cart-drawer__items` — le măsurăm pe amândouă și luăm maximul. */
    const overflowOf = (/** @type {Element|null} */ el) => (el ? el.scrollHeight - el.clientHeight : 0);
    const overflow = Math.max(
      overflowOf(this.querySelector('.cart-drawer__items')),
      overflowOf(this.querySelector('.cart-drawer__content'))
    );

    if (overflow > NIRU_FBT_OVERFLOW_TOLERANCE) details.open = false;

    this.#niruFbtWanted = details.open;
    details.dataset.niruFbt = details.open ? 'auto-open' : 'auto-collapsed';
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
